/**
 * Doc Chat Panel Component
 *
 * Generic chat/timeline panel for document detail pages.
 * Shows comments and activity in a timeline format.
 *
 * Responsive:
 * - Desktop: Right slide-over panel (inside main content)
 * - Mobile: Full-screen overlay
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useDocChat, useAddDocComment, useDeleteComment, useToggleReaction } from "../api/useDocChat";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { cn } from "@/lib/utils";
import { groupTimelineItems } from "../utils/chatGrouping";
import { ChatTimeline } from "./chat/ChatTimeline";
import { ChatComposer } from "./chat/ChatComposer";
import type { DocChatItem } from "@/lib/erp/docs";

type DocChatPanelProps = {
  doctype: string;
  name: string;
  onClose?: () => void;
};

type ChatFilterMode = "all" | "comments";

export function DocChatPanel({ doctype, name, onClose }: DocChatPanelProps) {
  const [filterMode, setFilterMode] = useState<ChatFilterMode>("all");
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [replyTo, setReplyTo] = useState<DocChatItem | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLElement>>(new Map());
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useDocChat(doctype, name);
  const addCommentMutation = useAddDocComment(doctype, name);
  const deleteCommentMutation = useDeleteComment(doctype, name);
  const toggleReactionMutation = useToggleReaction(doctype, name);
  const { data: currentUser } = useCurrentUser();
  const currentUserId = currentUser?.email || currentUser?.name;

  const items = data?.items ?? [];
  const hasMore = data?.hasMore ?? false;
  const prevItemsLengthRef = useRef(items.length);
  const hasInitialScrolledRef = useRef(false);

  // Group and insert date dividers (filter + group in one memo)
  const timelineEntries = useMemo(() => {
    const filtered = filterMode === "all"
      ? items
      : items.filter((item) => item.type === "comment");
    return groupTimelineItems(filtered);
  }, [items, filterMode]);

  // Scroll behavior:
  // - On first load: scroll to bottom once
  // - When sending a new message: scroll to bottom
  // - When loading older messages: keep current position
  useEffect(() => {
    const itemsLength = data?.items?.length ?? 0;

    if (!scrollContainerRef.current || !data?.items) {
      prevItemsLengthRef.current = itemsLength;
      return;
    }

    if (!hasInitialScrolledRef.current && itemsLength > 0) {
      requestAnimationFrame(() => {
        if (!scrollContainerRef.current) return;
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "auto",
        });
      });
      hasInitialScrolledRef.current = true;
      prevItemsLengthRef.current = itemsLength;
      return;
    }

    if (shouldScrollToBottom) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!scrollContainerRef.current) return;
          scrollContainerRef.current.scrollTo({
            top: scrollContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
          setShouldScrollToBottom(false);
        }, 150);
      });
    }

    prevItemsLengthRef.current = itemsLength;
  }, [data?.items?.length, shouldScrollToBottom]);

  // Scroll to bottom when filter mode changes
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    if (items.length === 0) return;

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 0);
  }, [filterMode]);

  const handleSend = useCallback((params: { content: string; mentions?: string[]; reply_to?: string }) => {
    addCommentMutation.mutate(params, {
      onSuccess: () => {
        setReplyTo(null);
        setShouldScrollToBottom(true);
      },
    });
  }, [addCommentMutation]);

  const handleLoadMore = async () => {
    await fetchNextPage();
  };

  const handleDelete = useCallback((commentName: string) => {
    deleteCommentMutation.mutate(commentName);
  }, [deleteCommentMutation]);

  const handleReact = useCallback((commentName: string, emoji: string) => {
    toggleReactionMutation.mutate({ commentName, emoji });
  }, [toggleReactionMutation]);

  const handleReply = useCallback((item: DocChatItem) => {
    setReplyTo(item);
  }, []);

  const registerMessageRef = useCallback((name: string, el: HTMLElement | null) => {
    if (el) {
      messageRefs.current.set(name, el);
    } else {
      messageRefs.current.delete(name);
    }
  }, []);

  const scrollToMessage = useCallback((commentName: string) => {
    const el = messageRefs.current.get(commentName);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-yellow-100/50");
    setTimeout(() => el.classList.remove("bg-yellow-100/50"), 1500);
  }, []);

  // ── Shared sub-components ──────────────────────────────────

  const filterTabs = (
    <div className="inline-flex items-center rounded-full bg-muted/60 p-1 text-xs">
      <button
        type="button"
        onClick={() => setFilterMode("all")}
        className={cn(
          "px-3 py-1 rounded-full transition-all",
          filterMode === "all"
            ? "bg-background shadow-sm text-foreground"
            : "text-muted-foreground"
        )}
      >
        All
      </button>
      <button
        type="button"
        onClick={() => setFilterMode("comments")}
        className={cn(
          "px-3 py-1 rounded-full transition-all",
          filterMode === "comments"
            ? "bg-background shadow-sm text-foreground"
            : "text-muted-foreground"
        )}
      >
        Comments
      </button>
    </div>
  );

  const closeButton = onClose ? (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClose}
      className="h-10 w-10 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 touch-manipulation"
    >
      <X className="h-5 w-5" />
    </Button>
  ) : null;

  const timeline = (
    <ChatTimeline
      entries={timelineEntries}
      currentUserId={currentUserId}
      isLoading={isLoading}
      isEmpty={items.length === 0}
      hasMore={hasMore}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={handleLoadMore}
      onDelete={handleDelete}
      onReact={handleReact}
      onReply={handleReply}
      registerMessageRef={registerMessageRef}
      scrollToMessage={scrollToMessage}
    />
  );

  const composer = (
    <ChatComposer
      doctype={doctype}
      name={name}
      replyTo={replyTo}
      onClearReply={() => setReplyTo(null)}
      onSend={handleSend}
      isPending={addCommentMutation.isPending}
    />
  );

  return (
    <>
      {/* Mobile: Full-screen overlay */}
      <div
        className="md:hidden fixed inset-0 z-50 bg-background flex flex-col"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
        }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Chat & Activity</h2>
            {filterTabs}
          </div>
          {closeButton}
        </div>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-4 py-4 min-h-0"
        >
          {timeline}
        </div>

        {composer}
      </div>

      {/* Desktop: Main content panel */}
      <div
        className="hidden md:flex flex-col h-full bg-white dark:bg-background rounded-2xl shadow-sm border border-border overflow-hidden w-full md:max-h-[calc(100vh-132px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:max-h-[calc(100vh-88px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]"
        style={{ height: "100%" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Chat & Activity</h2>
            {filterTabs}
          </div>
          {closeButton}
        </div>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-6 py-4 min-h-0 overscroll-contain"
        >
          {timeline}
        </div>

        {composer}
      </div>
    </>
  );
}
